import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import XLSX from "xlsx-js-style";
import { saveAs } from "file-saver";
import CustomButton from "./CustomButton";
import { BASE_URL } from "../config";
import { getAuthHeaders } from "../services/useAuth";
import Loader from "../dist/Loader";
import formatDate from "../utils/formatDate";
import "../css/AttendenceSheet.css";
const DCRPunchedAttendence = () => {
  const navigate = useNavigate();
  const [states, setStates] = useState([{ id: "All", name: "All" }]);
  const [divisions, setDivisions] = useState([{ id: "All", name: "All" }]);
  const [employees, setEmployees] = useState([
    { id: "All", first_name: "All", last_name: "" },
  ]);
  const [selectedState, setSelectedState] = useState("All");
  const [selectedDivision, setSelectedDivision] = useState("All");
  const [selectedEmployee, setSelectedEmployee] = useState("All");
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

  const [monthOptions] = useState([
    { value: 1, label: "January" },
    { value: 2, label: "February" },
    { value: 3, label: "March" },
    { value: 4, label: "April" },
    { value: 5, label: "May" },
    { value: 6, label: "June" },
    { value: 7, label: "July" },
    { value: 8, label: "August" },
    { value: 9, label: "September" },
    { value: 10, label: "October" },
    { value: 11, label: "November" },
    { value: 12, label: "December" },
  ]);
  const [yearOptions, setYearOptions] = useState([]);

  const [tableData, setTableData] = useState([]);
  const [showContainers, setShowContainers] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [daysInMonth, setDaysInMonth] = useState(31);
  const [searchParams] = useSearchParams();

  const LEAVE_STATUSES = [
    "A",
    "AB",
    "H",
    "T",
    "X",
    "M",
    "CL",
    "SL",
    "EL",
    "ML",
    "RL",
    "CO",
    "L",
    "WW",
    "W",
    "AWW",
    "ESI",
    "E.S.I",
    "E-S-I",
    "PR",
    "P",
  ];

  const normalizeStatus = (val = "") => {
    if (!val) return "";
    const str = val.toString().trim().toUpperCase();
    if (str === "ABSENT" || str === "AB" || str === "A") return "AB";
    if (str.includes("CL")) return "CL";
    if (str.includes("SL")) return "SL";
    if (str.includes("EL")) return "EL";
    if (str.includes("ML")) return "ML";
    if (str.includes("RL")) return "RL";
    if (str.includes("CO")) return "CO";
    if (str.includes("ESI")) return "ESI";
    return str;
  };

  const generateYears = () => {
    const startYear = 2018;
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let year = startYear; year <= currentYear; year++) years.push(year);
    setYearOptions(years);
  };

  const calculateDaysInMonth = useCallback(() => {
    const days = new Date(selectedYear, selectedMonth, 0).getDate();
    setDaysInMonth(days);
  }, [selectedMonth, selectedYear]);

  useEffect(() => {
    generateYears();
    calculateDaysInMonth();
    let mounted = true;
    const init = async () => {
      try {
        const [statesRes, divRes] = await Promise.all([
          axios.get(`${BASE_URL}/api/states`, { headers: getAuthHeaders() }),
          axios.get(`${BASE_URL}/api/divisions`, { headers: getAuthHeaders() }),
        ]);
        if (!mounted) return;
        setStates([{ id: "All", name: "All" }, ...(statesRes.data || [])]);
        setDivisions([{ id: "All", name: "All" }, ...(divRes.data || [])]);
      } catch (e) {
        setError("Failed to load dropdowns");
      }
    };
    init();
    return () => (mounted = false);
  }, [calculateDaysInMonth]);

  useEffect(() => {
    calculateDaysInMonth();
  }, [selectedMonth, selectedYear, calculateDaysInMonth]);
  useEffect(() => {
    setShowContainers(false);
    setTableData([]);
    setError(null);
  }, [selectedMonth, selectedYear, selectedState, selectedDivision]);

  const fetchEmployees = useCallback(
    async (stateId = "All", divisionName = "All") => {
      try {
        let url = `${BASE_URL}/api/employees`;
        const params = [];
        if (stateId && stateId !== "All")
          params.push(`state_id=${encodeURIComponent(stateId)}`);
        if (divisionName && divisionName !== "All")
          params.push(`division=${encodeURIComponent(divisionName)}`);
        if (params.length) url += `?${params.join("&")}`;
        const res = await axios.get(url, { headers: getAuthHeaders() });
        setEmployees([
          { id: "All", first_name: "All", last_name: "" },
          ...(Array.isArray(res.data) ? res.data : []),
        ]);
        setSelectedEmployee("All");
      } catch (err) {
        console.error("Error fetching employees:", err);
        setEmployees([{ id: "All", first_name: "All", last_name: "" }]);
      }
    },
    [],
  );

  const handleStateChange = async (stateId) => {
    setSelectedState(stateId);
    setSelectedDivision("All");
    setSelectedEmployee("All");
    setError(null);
    if (stateId === "All") {
      setDivisions((prev) => [
        { id: "All", name: "All" },
        ...prev.filter((d) => d.id !== "All"),
      ]);
      await fetchEmployees("All", "All");
      return;
    }
    try {
      const divisionRes = await axios.get(
        `${BASE_URL}/api/divisions?state_id=${stateId}`,
        { headers: getAuthHeaders() },
      );
      setDivisions([
        { id: "All", name: "All" },
        ...(Array.isArray(divisionRes.data) ? divisionRes.data : []),
      ]);
    } catch (err) {
      console.error("Error loading divisions", err);
      setDivisions([{ id: "All", name: "All" }]);
    }
    fetchEmployees(stateId, "All");
  };

  const handleDivisionChange = async (divisionId) => {
    setSelectedDivision(divisionId);
    setSelectedEmployee("All");
    setError(null);
    if (divisionId === "All") {
      await fetchEmployees(
        selectedState === "All" ? "All" : selectedState,
        "All",
      );
      return;
    }
    const division = divisions.find((d) => d.id == divisionId);
    const divisionName = division?.division_name || division?.name || null;
    fetchEmployees(
      selectedState === "All" ? "All" : selectedState,
      divisionName || "All",
    );
  };

  const getDayInfo = (row, day) => {
    const keys = [`day_${day}`, `d_${day}`, `${day}`];
    const daysObj = row.days || row;
    for (const k of keys) {
      if (daysObj && Object.prototype.hasOwnProperty.call(daysObj, k))
        return daysObj[k] ?? null;
    }
    return null;
  };

  const computeRowTotals = (row) => {
    let present_days = 0,
      leave_days = 0,
      holiday_days = 0,
      total_pob = 0,
      total_working_days = 0;
    for (let d = 1; d <= daysInMonth; d++) {
      const info = getDayInfo(row, d);
      if (!info) continue;
      const rawStatus = (info.status ?? info.day_name ?? "").toString().trim();
      const statusLower = rawStatus.toLowerCase();
      if (statusLower === "p" || statusLower === "present") {
        present_days++;
        total_working_days++;
      } else if (statusLower === "a" || statusLower === "absent") {
        leave_days++;
      } else if (
        statusLower === "sunday" ||
        statusLower === "holiday" ||
        statusLower === "h"
      ) {
        holiday_days++;
      } else if (statusLower === "w" || statusLower === "working") {
        total_working_days++;
      }
      const pob = Number(info.pob ?? info.pob_value ?? 0) || 0;
      total_pob += pob;
    }
    return {
      present_days,
      leave_days,
      holiday_days,
      total_pob,
      total_working_days,
    };
  };

  const isFutureMonth = () => {
    const today = new Date();
    return (
      selectedYear > today.getFullYear() ||
      (selectedYear === today.getFullYear() &&
        selectedMonth > today.getMonth() + 1)
    );
  };

  const handleGoClick = async () => {
    if (isFutureMonth()) {
      setTableData([]);
      setShowContainers(true);
      setError("No Data Found. Try Another Search.");
      return;
    }
    try {
      setIsLoading(true);
      setError(null);
      let url = `${BASE_URL}/api/reports/employee-attendance-summary?`;
      if (selectedState && selectedState !== "All")
        url += `state_id=${encodeURIComponent(selectedState)}&`;
      if (selectedDivision && selectedDivision !== "All") {
        const divisionObj = divisions.find((d) => d.id == selectedDivision);
        const divisionName =
          divisionObj?.division_name || divisionObj?.name || selectedDivision;
        url += `division=${encodeURIComponent(divisionName)}&`;
      }
      if (selectedMonth) url += `month=${selectedMonth}&`;
      if (selectedYear) url += `year=${selectedYear}&`;
      url = url.replace(/&$/, "");
      console.log("Calling attendance summary URL:", url);
      const response = await axios.get(url, { headers: getAuthHeaders() });
      console.log("Raw API response:", response);
      let payload = [];
      if (Array.isArray(response.data)) payload = response.data;
      else if (response.data && Array.isArray(response.data.data))
        payload = response.data.data;
      else if (response.data?.data && Array.isArray(response.data.data.records))
        payload = response.data.data.records;
      else if (response.data && typeof response.data === "object") {
        const firstArray = Object.values(response.data).find((v) =>
          Array.isArray(v),
        );
        payload = firstArray || [];
      }
      console.log("Normalized payload (array):", payload);
      setTableData(payload);
      setShowContainers(true);
    } catch (err) {
      console.error("Failed to fetch data", err);
      if (err.response)
        setError(
          `Server error: ${err.response.status} - ${err.response.statusText}`,
        );
      else if (err.request) setError("No response received from server");
      else setError(`Request error: ${err.message}`);
      setTableData([]);
      setShowContainers(false);
    } finally {
      setIsLoading(false);
    }
  };

  // ── Day Headers ──────────────────────────────────────────────────────────────
  const renderDayHeaders = () => {
    const headers = [];
    const dayLetters = ["S", "M", "T", "W", "T", "F", "S"];
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(selectedYear, selectedMonth - 1, day);
      const dayOfWeek = date.getDay();
      const dayLetter = dayLetters[dayOfWeek];
      const isSunday = dayOfWeek === 0;
      headers.push(
        <th key={`day-${day}`} className={isSunday ? "col-sun" : ""}>
          <div className="day-num">{day}</div>
          <div className="day-lbl">{dayLetter}</div>
        </th>,
      );
    }
    return headers;
  };

  const isSundayCol = (day) => {
    const date = new Date(selectedYear, selectedMonth - 1, day);
    return date.getDay() === 0;
  };
  const getDayCellClass = (statusText, isSunday, isAfterResign) => {
    if (isAfterResign) return "day-after-resign";
    if (isSunday) return "col-sun";
    const s = (statusText || "").toString().toUpperCase().trim();
    if (s === "P" || s === "PRESENT" || s === "PR") return "day-present";
    const leaveTypes = [
      "A",
      "AB",
      "CL",
      "SL",
      "EL",
      "ML",
      "RL",
      "CO",
      "ESI",
      "E.S.I",
      "E-S-I",
      "WW",
      "AWW",
      "L",
      "T",
      "X",
      "M",
      "PR",
    ];
    if (leaveTypes.includes(s) || s === "ABSENT") return "day-leave";
    return "";
  };
  const renderDayCells = (row) => {
    const cells = [];
    let resignDay = null;
    for (let d = 1; d <= daysInMonth; d++) {
      const info = getDayInfo(row, d);
      const s = (info ? (info.status ?? info.day_name ?? "") : "")
        .toString()
        .toUpperCase()
        .trim();

      if (s === "R") {
        resignDay = d;
        break;
      }
    }

    const joiningDateRaw = row.doj; // DOJ key

    const today = new Date();
    const futureMonth =
      selectedYear > today.getFullYear() ||
      (selectedYear === today.getFullYear() &&
        selectedMonth > today.getMonth() + 1);

    for (let day = 1; day <= daysInMonth; day++) {
      const info = getDayInfo(row, day);
      const statusText = info ? (info.status ?? info.day_name ?? "") : "";
      const sun = isSundayCol(day);

      let isBeforeJoining = false;
      let isAfterResign = false;

      // 🔵 Joining Logic
      if (joiningDateRaw) {
        const joiningDate = new Date(joiningDateRaw);

        const selectedStart = new Date(selectedYear, selectedMonth - 1, 1);
        const selectedEnd = new Date(selectedYear, selectedMonth, 0);

        if (selectedEnd < joiningDate) {
          // Entire month before joining
          isBeforeJoining = true;
        } else if (
          joiningDate.getFullYear() === selectedYear &&
          joiningDate.getMonth() + 1 === selectedMonth
        ) {
          // Same month partial
          isBeforeJoining = day < joiningDate.getDate();
        }
      }

      // 🔵 Resign Logic
      if (resignDay !== null && day >= resignDay) {
        isAfterResign = true;
      }

      const cellClass =
        futureMonth || isBeforeJoining || isAfterResign
          ? "day-after-resign"
          : getDayCellClass(statusText, sun, false);

      const dateStr = `${selectedYear}-${String(selectedMonth).padStart(2, "0")}-${String(day).padStart(2, "0")}`;

      const clickable = !futureMonth && !isBeforeJoining && !isAfterResign;

      cells.push(
        <td
          key={`day-${day}-${row.employee_id || row.id}`}
          className={`att-day-cell ${cellClass}${clickable ? " clickable-day" : ""}`}
          onClick={() =>
            clickable &&
            handleDayClick(row.employee_id || row.id, dateStr, statusText)
          }
          title={clickable ? "Click to view DCR details" : statusText}
          style={clickable ? { cursor: "pointer" } : {}}
        >
          {statusText}
        </td>,
      );
    }

    return cells;
  };

  const handleDayClick = (employeeId, dateStr, status) => {
    const url = `/admin/Pobdatewise-attendancedata?employee_id=${employeeId}&date=${dateStr}&status=${status}`;
    window.open(
      url,
      "_blank",
      "width=800,height=600,menubar=no,toolbar=no,status=no,scrollbars=yes,resizable=yes",
    );
  };

  const uniqueEmployees = Array.isArray(tableData)
    ? Array.from(
      new Map(
        tableData.map((emp) => [
          emp.employee_id || emp.id || emp.employeeCode || Math.random(),
          emp,
        ]),
      ).values(),
    )
    : [];

  const exportToExcel = () => {
  try {
    const exportData = uniqueEmployees.map((row, index) => {
      const totals = row.summary_counts || {};
      const empRaw = String(row.employee ?? row.id ?? "").trim();
      const empIsNum = /^\d+$/.test(empRaw);
      const empID = empIsNum ? Number(empRaw) : empRaw;

      const rowData = {
        "S.No": index + 1,
        "Emp ID": empID,
        State: row.state || "-",
        "Sub State": row.sub_state || "-",
        Employee: `${row.employee_name || row.name || "-"}${row.status === "RESIGNED" ? " (R)" : ""}`,
        Mobile: row.mobile || "-",
        DOJ: null,
        DOR: null,
        Manager: row.manager || "-",
        Desi: row.designation || "-",
        "City/HQ": row.city_hq || row.city || row.hq || "-",
        Division: row.division || row.division_name || "-",
      };

      for (let day = 1; day <= daysInMonth; day++) {
        const info = getDayInfo(row, day);
        const date = new Date(selectedYear, selectedMonth - 1, day);
        const dayLetters = ["S", "M", "T", "W", "T", "F", "S"];
        const dayLetter = dayLetters[date.getDay()];
        rowData[`${day} ${dayLetter}`] = info?.status ?? info?.day_name ?? "";
      }

      rowData["P"] = totals.P || 0;
      rowData["T"] = totals.T || 0;
      rowData["X"] = totals.X || 0;
      rowData["M"] = totals.M || 0;
      rowData["W"] = totals.W || 0;
      rowData["H"] = totals.H || 0;
      rowData["S"] = totals.S || 0;
      rowData["WD"] = totals.WD || 0;
      rowData["SL"] = totals.SL || 0;
      rowData["CL"] = totals.CL || 0;
      rowData["EL"] = totals.EL || 0;
      rowData["ML"] = totals.ML || 0;
      rowData["RL"] = totals.RL || 0;
      rowData["CO"] = totals.CO || 0;
      rowData["ESI"] = totals.ESI || 0;
      rowData["L"] = totals.L ?? row.leave_days ?? 0;
      rowData["A"] = totals.A ?? 0;
      rowData["AB"] = totals.AB ?? 0;
      rowData["WW"] = totals.WW ?? 0;
      rowData["A WW"] = totals.A_WW ?? 0;
      rowData["DCR Fy Leave"] = totals.DCR_Leave ?? 0;
      rowData["DCR Fy AB"] = totals.DCR_AB ?? 0;
      rowData["Remarks"] = row.remarks || "";

      return rowData;
    });

    const ws = XLSX.utils.json_to_sheet(exportData);

    const headerStyle = {
      font: { bold: true, color: { rgb: "FFFFFFFF" }, sz: 11 },
      fill: { fgColor: { rgb: "FF2563EB" } },
      alignment: { horizontal: "center", vertical: "center", wrapText: true },
      border: {
        top: { style: "thin", color: { rgb: "FF000000" } },
        bottom: { style: "thin", color: { rgb: "FF000000" } },
        left: { style: "thin", color: { rgb: "FF000000" } },
        right: { style: "thin", color: { rgb: "FF000000" } },
      },
    };
    const rowStyle = {
      alignment: { horizontal: "center", vertical: "center" },
      border: {
        top: { style: "thin", color: { rgb: "000000" } },
        bottom: { style: "thin", color: { rgb: "000000" } },
        left: { style: "thin", color: { rgb: "000000" } },
        right: { style: "thin", color: { rgb: "000000" } },
      },
    };

    const setDateCell = (r, c, rawVal, s) => {
      const addr = XLSX.utils.encode_cell({ r, c });
      if (rawVal) {
        try {
          const date = new Date(
            /^\d{4}-\d{2}-\d{2}$/.test(String(rawVal))
              ? `${rawVal}T00:00:00`
              : rawVal
          );
          if (!isNaN(date.getTime())) {
            ws[addr] = { v: date, t: "d", z: "dd-mmm-yyyy", s };
            return;
          }
        } catch {}
      }
      ws[addr] = { v: "", t: "s", s };
    };

    const range = XLSX.utils.decode_range(ws["!ref"]);

    for (let R = range.s.r; R <= range.e.r; ++R) {
      for (let C = range.s.c; C <= range.e.c; ++C) {
        const cellAddress = XLSX.utils.encode_cell({ r: R, c: C });

        if (!ws[cellAddress]) {
          ws[cellAddress] = { t: "z", v: "" };
        }

        ws[cellAddress].s = R === 0 ? headerStyle : rowStyle;
      }
    }

    const allKeys = Object.keys(exportData[0] || {});
    const dojColIndex = allKeys.indexOf("DOJ");
    const dorColIndex = allKeys.indexOf("DOR");

    uniqueEmployees.forEach((row, index) => {
      const r = index + 1;
      setDateCell(r, dojColIndex, row.final_joining_date || row.doj, rowStyle);
      setDateCell(r, dorColIndex, row.resign_date || row.dor, rowStyle);
    });

    ws["!cols"] = allKeys.map((key) => {
      if (["S.No"].includes(key)) return { wch: 6 };
      if (["Emp ID", "Mobile"].includes(key)) return { wch: 14 };
      if (["Employee"].includes(key)) return { wch: 28 };
      if (["State", "Sub State"].includes(key)) return { wch: 15 };
      if (["Manager"].includes(key)) return { wch: 22 };
      if (["DOJ", "DOR"].includes(key)) return { wch: 13 };
      if (["Remarks"].includes(key)) return { wch: 20 };
      return { wch: 8 };
    });

    ws["!freeze"] = { xSplit: 0, ySplit: 1 };

    ws["!autofilter"] = {
      ref: `A1:${XLSX.utils.encode_col(range.e.c)}1`,
    };

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Attendance Summary");

    const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });

    const data = new Blob([excelBuffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    const monthName =
      monthOptions.find((m) => m.value === selectedMonth)?.label || selectedMonth;

    saveAs(data, `Attendance_Summary_${monthName}_${selectedYear}.xlsx`);

  } catch (e) {
    console.error("Export failed", e);
    setError("Failed to export to Excel");
  }
};

  const handlePrint = () => {
    try {
      const printWindow = window.open("", "_blank");
      if (!printWindow) {
        setError("Pop up blocked. Please allow pop ups to enable printing.");
        return;
      }
      const tableHtml =
        document.querySelector(".att-wrap")?.innerHTML ||
        "<div>No table available</div>";
      const monthName =
        monthOptions.find((m) => m.value === selectedMonth)?.label || "";
      const stateName =
        states.find((s) => s.id == selectedState)?.name || "All";
      const divisionObj = divisions.find((d) => d.id == selectedDivision);
      const divisionName =
        divisionObj?.division_name || divisionObj?.name || "All";
      printWindow.document.write(`
        <html><head><title>Attendance Summary Report</title>
        <style>
          @page { size: landscape; margin: 8mm; }
          body { font-family: Arial, sans-serif; margin: 0; padding: 10px; font-size: 10px; }
          .print-title { font-size: 16px; font-weight: bold; margin-bottom: 4px; }
          .print-filters { font-size: 12px; margin-bottom: 10px; }
          table { width: 100%; border-collapse: collapse; font-size: 9px; }
          th, td { border: 1px solid #bbb; padding: 2px 3px; text-align: center; }
          th { background-color: #4472C4; color: #fff; font-weight: bold; }
          .col-sun { background-color: #90EE90 !important; color: #000 !important; }
          .day-present { color: #008000 !important; font-weight: bold; }
          .day-leave { color: #cc0000 !important; font-weight: bold; }
          .day-after-resign { background-color: #6495ED !important; color: #fff !important; }
          .row-resigned td { color: #cc0000 !important; font-weight: bold; }
          .sum-absent { color: #cc0000 !important; font-weight: bold; }
          tr:nth-child(odd) td { background-color: #fff; }
          tr:nth-child(even) td { background-color: #f2f2f2; }
        </style></head><body>
        <div class="print-title">Attendance Summary Report</div>
        <div class="print-filters">
          State: ${stateName} | Division: ${divisionName} | Period: ${monthName} ${selectedYear} | Generated: ${new Date().toLocaleString()}
        </div>
        ${tableHtml}
        <script>window.onload=function(){setTimeout(function(){window.print();},250);}<\/script>
        </body></html>`);
      printWindow.document.close();
    } catch (e) {
      console.error("Print failed", e);
      setError("Failed to open print window");
    }
  };

  return (
    <div
      style={{
        border: "1px solid #d1d5db",
        borderRadius: "6px",
        background: "#fff",
        padding: "2px",
        boxSizing: "border-box",
        width: "100%",
      }}
    >
      {isLoading && <Loader onCancel={() => setIsLoading(false)} />}

      <div className="p-4 border-2 border-gray-300 rounded-lg bg-gray-50">
        <h1 className="text-xl sm:text-2xl font-bold text-center text-gray-800 mb-4">
          <span className="text-blue-700"> Attendance </span>
          <span className="text-blue-500">Sheet</span>
        </h1>

        <div className="AttendenceSheet-filter-row1">
          <label className="AttendenceSheet-label">State</label>
          <select
            className="AttendenceSheet-dropdown"
            value={selectedState}
            onChange={(e) => handleStateChange(e.target.value)}
            disabled={isLoading}
            required
          >
            {states.map((state) => (
              <option key={state.id} value={state.id}>
                {state.name}
              </option>
            ))}
          </select>
          <label className="AttendenceSheet-label">Division</label>
          <select
            className="AttendenceSheet-dropdown"
            value={selectedDivision}
            onChange={(e) => handleDivisionChange(e.target.value)}
            disabled={!selectedState || isLoading}
          >
            {divisions.map((division) => (
              <option key={division.id} value={division.id}>
                {division.division_name || division.name}
              </option>
            ))}
          </select>
        </div>

        <div className="AttendenceSheet-filter-row2">
          <label className="AttendenceSheet-label">Year</label>
          <select
            className="AttendenceSheet-dropdown"
            value={selectedYear}
            onChange={(e) => setSelectedYear(parseInt(e.target.value))}
            disabled={isLoading}
            required
          >
            {yearOptions.map((year) => (
              <option key={year} value={year}>
                {year}
              </option>
            ))}
          </select>
          <label className="AttendenceSheet-label">Month</label>
          <select
            className="AttendenceSheet-dropdown"
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
            disabled={isLoading}
            required
          >
            {monthOptions.map((month) => (
              <option key={month.value} value={month.value}>
                {month.label}
              </option>
            ))}
          </select>
        </div>

        <div className="flex items-center justify-center mt-2">
          <CustomButton
            variant="go"
            label={isLoading ? "Loading..." : "GO"}
            onClick={handleGoClick}
            disabled={isLoading}
          />
        </div>

        {error && <div className="att-error">{error}</div>}
      </div>

      {showContainers && (
        <div style={{ marginTop: "12px" }}>
          <div className="att-page-wrap">
            <div className="att-wrap">
              <table className="att-table unlock-dcr-leave-table">
                <colgroup>
                  <col style={{ width: "22px" }} />
                  <col style={{ width: "34px" }} />
                  <col style={{ width: "34px" }} />
                  <col style={{ width: "34px" }} />
                  <col style={{ width: "58px" }} />
                  <col style={{ width: "52px" }} />
                  <col style={{ width: "28px" }} />
                  <col style={{ width: "28px" }} />
                  <col style={{ width: "44px" }} />
                  <col style={{ width: "26px" }} />
                  <col style={{ width: "42px" }} />
                  <col style={{ width: "42px" }} />
                  {Array.from({ length: daysInMonth }, (_, i) => (
                    <col key={`dc-${i}`} style={{ width: "13px" }} />
                  ))}
                  {Array.from({ length: 20 }, (_, i) => (
                    <col key={`sc-${i}`} style={{ width: "13px" }} />
                  ))}
                  <col style={{ width: "20px" }} />
                  <col style={{ width: "20px" }} />
                  <col style={{ width: "36px" }} />
                </colgroup>

                <thead>
                  <tr>
                    <th>S.No</th>
                    <th>Emp ID</th>
                    <th>State</th>
                    <th>Sub State</th>
                    <th>Employee</th>
                    <th>Mobile</th>
                    <th>DOJ</th>
                    <th>DOR</th>
                    <th>Manager</th>
                    <th>Desi</th>
                    <th>City/HQ</th>
                    <th>Division</th>
                    {renderDayHeaders()}
                    <th>P</th>
                    <th>T</th>
                    <th>X</th>
                    <th>M</th>
                    <th>W</th>
                    <th>H</th>
                    <th>S</th>
                    <th>WD</th>
                    <th>SL</th>
                    <th>CL</th>
                    <th>EL</th>
                    <th>ML</th>
                    <th>RL</th>
                    <th>CO</th>
                    <th>ESI</th>
                    <th>L</th>
                    <th>A</th>
                    <th>AB</th>
                    <th>WW</th>
                    <th>A WW</th>
                    <th>DCR Fy Leave</th>
                    <th>DCR Fy AB</th>
                    <th>Remarks</th>
                  </tr>
                </thead>

                <tbody>
                  {uniqueEmployees.length > 0 ? (
                    uniqueEmployees.map((row, index) => {
                      const totals = row.summary_counts || {};
                      const isResigned = row.status === "RESIGNED";
                      return (
                        <tr
                          key={index}
                          className={isResigned ? "row-resigned" : ""}
                        >
                          <td>{index + 1}</td>
                          <td>{row.employee || row.id || "-"}</td>
                          <td>{row.state || "-"}</td>
                          <td>{row.sub_state || "-"}</td>
                          <td>
                            {row.employee_name || row.name || "-"}
                            {isResigned && (
                              <span style={{ color: "#cc0000" }}> (R)</span>
                            )}
                          </td>
                          <td>
                            {row.mobile ? (
                              <a
                                href={`tel:${row.mobile}`}
                                className="text-blue-600 underline hover:text-blue-800"
                              >
                                {row.mobile}
                              </a>
                            ) : (
                              "-"
                            )}
                          </td>
                          <td>
                            {formatDate(row.final_joining_date || row.doj) ||
                              "-"}
                          </td>
                          <td>
                            {formatDate(row.resign_date || row.dor) || "-"}
                          </td>
                          <td>{row.manager || "-"}</td>
                          <td title={row.designation || "-"}>
                            {row.designation || "-"}
                          </td>
                          <td>{row.city_hq || row.city || row.hq || "-"}</td>
                          <td>{row.division || row.division_name || "-"}</td>
                          {renderDayCells(row)}
                          <td>{totals.P || 0}</td>
                          <td>{totals.T || 0}</td>
                          <td>{totals.X || 0}</td>
                          <td>{totals.M || 0}</td>
                          <td>{totals.W || 0}</td>
                          <td>{totals.H || 0}</td>
                          <td>{totals.S || 0}</td>
                          <td>{totals.WD || 0}</td>
                          <td>{totals.SL || 0}</td>
                          <td>{totals.CL || 0}</td>
                          <td>{totals.EL || 0}</td>
                          <td>{totals.ML || 0}</td>
                          <td>{totals.RL || 0}</td>
                          <td>{totals.CO || 0}</td>
                          <td>{totals.ESI || 0}</td>
                          <td>{row.leave_days ?? 0}</td>
                          <td className="sum-absent">{totals.A ?? 0}</td>
                          <td className="sum-absent">{totals.AB ?? 0}</td>
                          <td>{totals.WW ?? 0}</td>
                          <td>{totals.A_WW ?? 0}</td>
                          <td>{row.summary_counts?.DCR_Leave ?? 0}</td>
                          <td>{row.summary_counts?.DCR_AB ?? 0}</td>
                          <td>{row.remarks || ""}</td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr className="att-no-data">
                      <td colSpan={12 + daysInMonth + 23}>
                        {error || "No Data Found. Try Another Search."}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <div className="mt-4 flex gap-4 justify-center">
            <CustomButton onClick={handlePrint} label="Print" variant="print">
              🖨
            </CustomButton>
            <CustomButton
              onClick={exportToExcel}
              label="Export"
              variant="export"
            >
              📥
            </CustomButton>
          </div>
        </div>
      )}
    </div>
  );
};

export default DCRPunchedAttendence;
